import type { PerformancePerson } from '@gokrew/models/performance';
import { AVATAR_COLORS } from '@/constants/avatar-colors';
import { cn, getColorForText } from '@/lib/utils';
import { getNameFromObj, getUserInitials } from '@/utility/utility';
import styles from './PersonBadge.module.css';

export interface PersonBadgeProps {
  /** `null` renders the fallback label, e.g. an anonymous peer. */
  person: PerformancePerson | null;
  fallbackLabel?: string;
  /** Second line; defaults to the person's designation. */
  subtitle?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/** Initials avatar + name, the way people appear across performance screens. */
export const PersonBadge = ({
  person,
  fallbackLabel = '',
  subtitle,
  size = 'md',
  className,
}: PersonBadgeProps) => {
  const name = person ? getNameFromObj(person) : fallbackLabel;
  const initials = person ? getUserInitials(person) : '?';
  const second = subtitle === undefined ? person?.designation : subtitle;
  return (
    <div className={cn(styles.badge, className)}>
      <span
        className={cn(styles.avatar, styles[size], !person && styles.unknown)}
        style={
          person
            ? { backgroundColor: getColorForText(name, AVATAR_COLORS) }
            : undefined
        }
        aria-hidden
      >
        {initials}
      </span>
      <span className={styles.text}>
        <span className={styles.name}>{name}</span>
        {second && <span className={styles.subtitle}>{second}</span>}
      </span>
    </div>
  );
};
